import {
  ACCENT_COLORS,
  accentSeedColor,
  AppearancePreviewArt,
  Box,
  Button,
  featuredThemes,
  FieldTrigger,
  OptionSelect,
  PreviewRadioGroup,
  seedContrast,
  seedWithBackground,
  seedWithContrast,
  Slider,
  Text,
  THEME_CATALOG,
  themeBackgroundOptions,
  ThemeCatalogPicker,
  ThemeColorField,
  themeSeedFor,
  ThemeSwatchStrip,
  unfeaturedThemes,
  type ThemeCatalogEntry,
  type ThemeColorMode,
  type ThemeColorOption,
} from "@salestrak/ui";
import React from "react";

import {
  SettingsGroup,
  SettingsGroupRow,
} from "#app/components/settings/settings-group";
import { useThemeAppearance } from "#app/components/theme/theme-appearance-provider";
import {
  PALETTE_DEFAULT,
  paletteSeed,
  useEditedThemeColors,
  useResetAppearance,
  useThemeAppearanceMode,
  useThemeEditingMode,
  useThemePalette,
} from "#app/components/theme/use-theme-appearance-state";

export function ThemeModeSection({
  mode,
}: {
  mode: ThemeColorMode;
}): React.ReactElement {
  return (
    <SettingsGroup>
      <SettingsGroupRow
        header
        label={mode === "light" ? "Light theme" : "Dark theme"}
      >
        <ThemeCatalogField />
      </SettingsGroupRow>

      {/* A theme supplies all three, and any of them can be overwritten.
          Editing one makes the half yours; picking a theme again reseeds it. */}
      <SettingsGroupRow label="Accent" quiet>
        <ThemeAccentField />
      </SettingsGroupRow>

      <SettingsGroupRow label="Background" quiet>
        <ThemeBackgroundField />
      </SettingsGroupRow>

      <SettingsGroupRow label="Foreground" quiet>
        <ThemeForegroundField />
      </SettingsGroupRow>

      {/* Not a text control. It moves the surface ladder and the quiet ink
          around the Foreground above, which holds. */}
      <SettingsGroupRow label="Contrast" quiet>
        <ThemeContrastField />
      </SettingsGroupRow>

      {mode === "dark" ? (
        <SettingsGroupRow label="Navigation" quiet>
          <DarkNavField />
        </SettingsGroupRow>
      ) : null}

    </SettingsGroup>
  );
}

/**
 * Experiment control, live in every build: which way the dark navigation
 * planes travel.
 * Three live arrangements under evaluation — Recessed is the spine's (the
 * whole navigation ladder under the page); Lifted steps the pane toward the
 * ink (the reference app's themed panel) over a recessed rail canvas; Lifted,
 * flat canvas raises the pane the same way with the canvas AT the page, the
 * reference shell's own composition. The choice mirrors to localStorage
 * (`theme_dark_nav`) only; it is a session experiment, not a preference, and
 * this row is deleted when one arrangement wins.
 */
function DarkNavField(): React.ReactElement {
  const { darkNav, setDarkNav } = useThemeAppearance();
  return (
    <OptionSelect
      aria-label="Navigation"
      value={darkNav}
      onValueChange={(next) => {
        if (next === "recess" || next === "lift" || next === "lift-flat") {
          setDarkNav(next);
        }
      }}
      options={[
        { value: "recess", label: "Recessed" },
        { value: "lift", label: "Lifted" },
        { value: "lift-flat", label: "Lifted, flat canvas" },
      ]}
    />
  );
}

export function ThemeCatalogField({
  width = "auto",
}: { width?: "auto" | "full" } = {}): React.ReactElement {
  const { value, setValue } = useThemePalette();
  const resolvedTheme = useThemeEditingMode();

  // Each row's strip is the seed a pick would PAINT — the palette's own accent
  // included, because picking clears the workspace accent (see useThemePalette).
  const strip = React.useCallback(
    (key: string) => paletteSeed(key, resolvedTheme),
    [resolvedTheme],
  );

  // Three groups, in the order a decision gets made: what ships, what you made,
  // then the long list. Default and Custom are POSITIONS rather than catalog
  // entries, which is why neither is named in `FEATURED_THEME_KEYS` — that list
  // hoists catalog palettes up beside Default without touching this shape.
  const entries = React.useMemo<ThemeCatalogEntry[]>(() => {
    const fromCatalog = (
      theme: (typeof THEME_CATALOG)[number],
      section: string,
    ): ThemeCatalogEntry => ({
      key: theme.key,
      title: theme.title,
      section,
      colors: strip(theme.key),
      ...(theme[resolvedTheme]?.border === undefined
        ? {}
        : { border: theme[resolvedTheme]!.border }),
    });
    return [
      {
        key: PALETTE_DEFAULT,
        title: "Default",
        section: "Popular",
        colors: strip(PALETTE_DEFAULT),
      },
      // Only palettes that HAVE this half. A theme shipping dark only cannot
      // be chosen for light — picking it would silently paint the shipped
      // design and the row would lie about what it does. The reference filters
      // the same way, which is why its light list is the shorter one.
      ...featuredThemes()
        .filter((theme) => theme[resolvedTheme] !== null)
        .map((theme) => fromCatalog(theme, "Popular")),
      ...unfeaturedThemes()
        .filter((theme) => theme[resolvedTheme] !== null)
        .map((theme) => fromCatalog(theme, "Built-in")),
    ];
  }, [resolvedTheme, strip]);

  const active = entries.find((entry) => entry.key === value) ?? entries[0]!;

  return (
    <ThemeCatalogPicker
      entries={entries}
      onValueChange={setValue}
      trigger={
        <ThemeCatalogTrigger
          active={active}
          label={active.title}
          width={width}
        />
      }
      value={value}
    />
  );
}

/** The trigger, in the exact SelectTrigger anatomy so it is indistinguishable
 *  from the selects it sits beside: glyph · flex-1 value · chevron. */
function ThemeCatalogTrigger({
  active,
  label,
  width,
  ...props
}: {
  active: ThemeCatalogEntry;
  label: string;
  width: "auto" | "full";
}): React.ReactElement {
  return (
    <FieldTrigger
      aria-label="Theme"
      leading={
        <ThemeSwatchStrip
          {...(active.border === undefined ? {} : { border: active.border })}
          colors={active.colors}
        />
      }
      value={label}
      width={width}
      {...props}
    />
  );
}

/**
 * The body ink, as its own row.
 *
 * Editable because the reference application makes it editable and a theme
 * supplies it like the other two. Writing it directly rather than through a
 * dial is safe: the CONTRAST slider does not touch this value, so the two
 * controls cannot fight — the dial moves the surfaces and the quiet rungs
 * around whatever ink is here.
 */
export function ThemeForegroundField(): React.ReactElement {
  const colors = useEditedThemeColors();
  return (
    <ThemeColorField
      aria-label="Foreground"
      onChange={(color) => colors.preview({ ...colors.value, foreground: color })}
      onChangeComplete={(color) =>
        colors.commit({ ...colors.value, foreground: color })
      }
      pickerSize={248}
      value={colors.value.foreground}
    />
  );
}

/**
 * The accent options: the theme's own first, then the shipped hues.
 *
 * "Neutral" is the accent the CHOSEN THEME supplies, not a fixed blue — the
 * same rule the palette list follows, so one click is always the way back to
 * whatever the theme decided.
 */
function themeAccentOptions(
  mode: ThemeColorMode,
  themeAccent: string,
): readonly ThemeColorOption[] {
  return [
    // Neutral is the shipped NEUTRAL pair — a near-black button in light, a
    // white one in dark — with the hue showing only where it is ink. It is a
    // named option rather than an absence so the accent field has a way back.
    { name: "Neutral", neutral: true, value: themeAccent },
    ...ACCENT_COLORS.map((accent) => ({
      name: accent.charAt(0).toUpperCase() + accent.slice(1),
      value: accentSeedColor(accent, mode),
    })).filter((option) => option.value !== themeAccent),
  ];
}

/**
 * The appearance choice as three preview cards, the way the reference does it.
 *
 * A picture rather than a word because the choice IS a picture: a viewer is
 * deciding what the interface will look like, and three thumbnails answer that
 * in one glance where a select asks them to imagine it. The System card is
 * split down the middle, which is the only honest way to draw "whichever the
 * machine is using".
 */
export function ThemeAppearanceCards({
  compact,
}: { compact?: boolean } = {}): React.ReactElement {
  const { value, setValue } = useThemeAppearanceMode();
  return (
    <PreviewRadioGroup
      aria-label="Appearance"
      compact={compact}
      onValueChange={(next) => {
        if (next === "system" || next === "light" || next === "dark") {
          setValue(next);
        }
      }}
      options={[
        {
          art: <AppearancePreviewArt variant="system" />,
          label: "System",
          value: "system",
        },
        {
          art: <AppearancePreviewArt variant="light" />,
          label: "Light",
          value: "light",
        },
        {
          art: <AppearancePreviewArt variant="dark" />,
          label: "Dark",
          value: "dark",
        },
      ]}
      value={value}
    />
  );
}

/**
 * The accent as a COLOR FIELD, like Background and Foreground beside it.
 *
 * Editing it writes the seed and clears the workspace accent, so the value on
 * screen is the value painting. Leaving the named axis applying on top would
 * make a hex a user just chose silently do nothing.
 */
export function ThemeAccentField(): React.ReactElement {
  const colors = useEditedThemeColors();
  const resolvedTheme = useThemeEditingMode();
  const { committed, preview, commit } = useThemeAppearance();
  const themeAccent = themeSeedFor(
    committed.theme[resolvedTheme],
    resolvedTheme,
  ).accent;
  const neutral = colors.value.neutralPrimary === true;

  const write = (color: string, neutralPrimary: boolean, persist: boolean) =>
    (persist ? commit : preview)({
      ...committed,
      accent: null,
      custom: {
        ...committed.custom,
        [resolvedTheme]: { ...colors.value, accent: color, neutralPrimary },
      },
    });
  // Picking Neutral RESTORES the pair; picking a hue means the hue paints the
  // primary, which is the whole difference between the two. The field hands
  // back the OPTION that was clicked, so this reads the flag rather than
  // inferring it from a colour two options could share. The free picker sends
  // no option, which is correctly not the neutral.
  const pick = (
    color: string,
    option: ThemeColorOption | undefined,
    persist: boolean,
  ) => write(color, option?.neutral === true, persist);

  return (
    <ThemeColorField
      aria-label="Accent"
      neutral={neutral}
      onChange={(color, option) => pick(color, option, false)}
      onChangeComplete={(color, option) => pick(color, option, true)}
      options={themeAccentOptions(resolvedTheme, themeAccent)}
      pickerSize={248}
      value={colors.value.accent}
    />
  );
}

/**
 * The custom background as a settings-row control: the library color field
 * (value-painted trigger, vetted options over the free picker) wired to the
 * resolved mode's seed. A label-less field — the settings row carries the
 * name.
 */
export function ThemeBackgroundField(): React.ReactElement {
  const colors = useEditedThemeColors();
  const resolvedTheme = useThemeEditingMode();
  return (
    <ThemeColorField
      aria-label="Background"
      onChange={(color) => colors.preview(seedWithBackground(colors.value, color, resolvedTheme))}
      onChangeComplete={(color) =>
        colors.commit(seedWithBackground(colors.value, color, resolvedTheme))
      }
      options={themeBackgroundOptions(resolvedTheme)}
      pickerSize={248}
      value={colors.value.background}
    />
  );
}

/** The contrast dial as a settings-row control: how far the surfaces sit apart
 *  and how loud the quiet ink reads. The body ink is not on this dial — it
 *  holds at the seed's foreground wherever the control is dragged. */
export function ThemeContrastField(): React.ReactElement {
  const colors = useEditedThemeColors();
  const contrast = seedContrast(colors.value);
  // Base UI types the callback `number | number[]`; a single-thumb slider
  // always reports the scalar, so the array arm is a type-level backstop.
  const first = (next: number | readonly number[]): number =>
    typeof next === "number" ? next : (next[0] ?? 0);
  return (
    <Box align="center" direction="row" gap="md" width={224}>
      <Slider
        aria-label="Contrast"
        max={100}
        min={0}
        onValueChange={(next) =>
          colors.preview(seedWithContrast(colors.value, first(next)))
        }
        onValueCommitted={(next) =>
          colors.commit(seedWithContrast(colors.value, first(next)))
        }
        value={contrast}
      />
      <Box justify="end" width={28}>
        <Text size="sm" variant="muted" tabular>
          {contrast}
        </Text>
      </Box>
    </Box>
  );
}

export function ThemeResetButton(): React.ReactElement {
  const reset = useResetAppearance();

  // Just "Reset": the settings row hosting this carries the full name.
  return (
    <Button variant="ghost" onClick={reset}>
      Reset
    </Button>
  );
}
